/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useEffect, useState } from '@web-stories-wp/react';

const DEFAULT_STATE = {
  elementId: null,
  revision: 0,
  onComplete: null,
};

let state = DEFAULT_STATE;
const listeners = new Set();

const emit = () => {
  listeners.forEach((listener) => listener(state));
};

export function getDynamicCropRecorderState() {
  return state;
}

export function subscribeDynamicCropRecorder(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function startDynamicCropRecording({ elementId, onComplete }) {
  state = {
    elementId,
    onComplete,
    revision: state.revision + 1,
  };
  emit();
}

export function cancelDynamicCropRecording() {
  state = {
    ...DEFAULT_STATE,
    revision: state.revision + 1,
  };
  emit();
}

export function completeDynamicCropRecording(positions) {
  const { onComplete } = state;
  state = {
    ...DEFAULT_STATE,
    revision: state.revision + 1,
  };
  emit();
  if (typeof onComplete === 'function') {
    onComplete(positions);
  }
}

export function useDynamicCropRecorderState() {
  const [recorderState, setRecorderState] = useState(
    getDynamicCropRecorderState
  );

  useEffect(() => subscribeDynamicCropRecorder(setRecorderState), []);

  return recorderState;
}
